import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AttendancePhoto } from "@/components/AttendancePhoto";
import { Users, Plus, Trash2, Pencil, Calendar, DollarSign, CheckCircle2, PlayCircle, Lock, LogIn, LogOut, RefreshCw, UserCheck, FileText, X, ChevronUp, ChevronDown, Clock3 } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === "string" ? d : d.msg || JSON.stringify(d))).join(", ");
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return "Error";
};

const FREQ = { weekly: "Semanal", biweekly: "Quincenal", monthly: "Mensual" };
const PAY_METHOD = { efectivo: "Efectivo", transferencia: "Transferencia", cheque: "Cheque" };

export default function Nomina() {
  const [tab, setTab] = useState("empleados");
  const [summary, setSummary] = useState({});
  const [employees, setEmployees] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [accounts, setAccounts] = useState([]);

  const load = async () => {
    const [s, e, p, a] = await Promise.all([
      api.get("/payroll/summary"),
      api.get("/employees"),
      api.get("/payroll/periods"),
      api.get("/treasury/accounts"),
    ]);
    setSummary(s.data); setEmployees(e.data); setPeriods(p.data);
    setAccounts(a.data.filter((x) => x.active !== false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="nomina-page">
      <header>
        <h1 className="text-3xl font-outfit font-bold text-stone-900 dark:text-stone-100 flex items-center gap-2.5"><Users className="w-7 h-7 text-[#1E3A8A]" /> Nómina</h1>
        <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">Empleados, corridas de nómina y cálculo ISR/IMSS/INFONAVIT. El checador vive en un módulo separado.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPI icon={UserCheck} label="Empleados activos" value={summary.active_employees || 0} tone="blue" />
        <KPI icon={LogIn} label="Entradas hoy" value={summary.today_checkins || 0} tone="emerald" />
        <KPI icon={DollarSign} label="Costo mes (neto)" value={money(summary.month_cost)} tone="amber" />
        <KPI icon={PlayCircle} label="Períodos abiertos" value={summary.open_periods || 0} tone="stone" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full flex-wrap h-auto justify-start gap-1 bg-stone-100 dark:bg-stone-900 p-1 rounded-lg">
          <TabsTrigger value="empleados" data-testid="tab-empleados" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Empleados</TabsTrigger>
          <TabsTrigger value="nomina" data-testid="tab-nomina" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Corridas de nómina</TabsTrigger>
          <TabsTrigger value="prestamos" data-testid="tab-prestamos" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Préstamos y deducciones</TabsTrigger>
          <TabsTrigger value="bonos" data-testid="tab-bonos" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Bonos</TabsTrigger>
          <TabsTrigger value="asistencia" data-testid="tab-asistencia" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Reporte de asistencia</TabsTrigger>
          <TabsTrigger value="horas-extra" data-testid="tab-horas-extra" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">Horas extra</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="empleados"><EmpleadosTab employees={employees} onChange={load} /></TabsContent>
          <TabsContent value="nomina">
            <NominaTab periods={periods} accounts={accounts} onChange={load} selected={selectedPeriod} setSelected={setSelectedPeriod} />
          </TabsContent>
          <TabsContent value="prestamos"><PrestamosTab employees={employees} /></TabsContent>
          <TabsContent value="bonos"><BonosTab employees={employees} /></TabsContent>
          <TabsContent value="asistencia"><ReporteAsistenciaTab employees={employees} /></TabsContent>
          <TabsContent value="horas-extra"><HorasExtraTab employees={employees} /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}


// ============================================================
// EMPLEADOS
// ============================================================
const EmpleadosTab = ({ employees, onChange }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", position: "", area: "", rfc: "", curp: "", nss: "", hire_date: new Date().toISOString().slice(0, 10), salary_base: 0, salary_frequency: "weekly", payment_method: "transferencia", bank_account: "", infonavit_type: "none", infonavit_value: 0, apply_isr: true, apply_imss: true, active: true, notes: "", expected_entry_time: "", standard_daily_hours: 8 };
  const [form, setForm] = useState(empty);
  const [toDelete, setToDelete] = useState(null);
  const [codes, setCodes] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Nombre requerido");
    try {
      const payload = { ...form, salary_base: parseFloat(form.salary_base) || 0, infonavit_value: parseFloat(form.infonavit_value) || 0, standard_daily_hours: parseFloat(form.standard_daily_hours) || 8 };
      if (editing) await api.put(`/employees/${editing.id}`, payload);
      else {
        const { data } = await api.post("/employees", payload);
        setCodes({ name: data.name, pin: data.pin, qr_token: data.qr_token });
      }
      toast.success(editing ? "Empleado actualizado" : "Empleado creado — anota su PIN");
      setOpen(false); onChange();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const regen = async (id) => {
    try {
      const { data } = await api.post(`/employees/${id}/regenerate-codes`);
      const emp = employees.find((e) => e.id === id);
      setCodes({ name: emp?.name, pin: data.pin, qr_token: data.qr_token });
      onChange();
    } catch (err) { toast.error("Error"); }
  };

  const del = async () => {
    try {
      const { data } = await api.delete(`/employees/${toDelete.id}`);
      toast.success(data.deactivated ? "Empleado desactivado" : "Empleado eliminado");
      setToDelete(null); onChange();
    } catch (err) { toast.error("Error"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Empleados</h3>
          <p className="text-xs text-stone-500 mt-0.5">{employees.length} registro(s) · {employees.filter((e) => e.active !== false).length} activo(s)</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="new-employee-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Nuevo empleado</Button>
      </div>

      <Card className="overflow-hidden border-stone-200 dark:border-stone-800 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
              <tr>{["Nombre", "Puesto/Área", "RFC", "Frecuencia", "Sueldo base", "Pago", "PIN", "Estado", ""].map((h) => <th key={h} className="px-3 py-3 text-left text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
              {employees.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-14 text-center">
                  <Users className="w-10 h-10 mx-auto text-stone-300 mb-2" strokeWidth={1.5} />
                  <div className="text-sm text-stone-500">Sin empleados registrados.</div>
                </td></tr>
              ) : employees.map((e) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`} className="hover:bg-stone-50 dark:hover:bg-stone-900/30">
                  <td className="px-3 py-3 text-sm font-medium">{e.name}</td>
                  <td className="px-3 py-3 text-xs">{e.position || "—"}{e.area && <span className="text-stone-500"> · {e.area}</span>}</td>
                  <td className="px-3 py-3 text-xs font-mono text-stone-600">{e.rfc || "—"}</td>
                  <td className="px-3 py-3 text-xs">{FREQ[e.salary_frequency] || e.salary_frequency}</td>
                  <td className="px-3 py-3 text-xs tabular-nums font-medium">{money(e.salary_base)}</td>
                  <td className="px-3 py-3 text-xs uppercase tracking-wider text-stone-500">{PAY_METHOD[e.payment_method] || e.payment_method}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => setCodes({ name: e.name, pin: e.pin, qr_token: e.qr_token })} className="font-mono text-sm font-bold text-[#1E3A8A] hover:underline" data-testid={`view-pin-${e.id}`}>{e.pin || "—"}</button>
                  </td>
                  <td className="px-3 py-3 text-xs">{e.active !== false ? <span className="inline-flex items-center gap-1 text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Activo</span> : <span className="text-stone-400">Inactivo</span>}</td>
                  <td className="px-3 py-3 text-xs">
                    <div className="flex gap-1">
                      <button onClick={() => { setEditing(e); setForm({ ...empty, ...e }); setOpen(true); }} className="p-1.5 rounded-md text-stone-500 hover:text-[#1E3A8A] hover:bg-blue-50" data-testid={`edit-emp-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => regen(e.id)} title="Regenerar PIN/QR" className="p-1.5 rounded-md text-stone-500 hover:text-amber-600 hover:bg-amber-50"><RefreshCw className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setToDelete(e)} className="p-1.5 rounded-md text-stone-500 hover:text-red-600 hover:bg-red-50" data-testid={`delete-emp-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Employee form modal */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-auto">
          <Card className="w-full max-w-2xl p-6 my-8">
            <h3 className="font-outfit font-bold text-lg mb-4">{editing ? "Editar empleado" : "Nuevo empleado"}</h3>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label="Nombre completo *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" /></F>
                <F label="Puesto"><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Operador, Chofer..." /></F>
                <F label="Área"><Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Producción, Ventas..." /></F>
                <F label="Fecha de ingreso"><Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} /></F>
                <F label="Hora esperada de entrada">
                  <Input type="time" value={form.expected_entry_time || ""} onChange={(e) => setForm({ ...form, expected_entry_time: e.target.value })} data-testid="emp-expected-entry" />
                  <span className="text-[10px] text-stone-500">Opcional. Se usa para marcar retardos (tolerancia de 15 min) en el Checador.</span>
                </F>
                <F label="Jornada estándar (horas/día)">
                  <Input type="number" min="1" max="24" step="0.5" value={form.standard_daily_hours} onChange={(e) => setForm({ ...form, standard_daily_hours: e.target.value })} data-testid="emp-standard-hours" />
                  <span className="text-[10px] text-stone-500">Se usa para detectar horas extra en el Checador y autocompletarlas en la nómina semanal.</span>
                </F>
                <F label="RFC"><Input value={form.rfc} onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })} /></F>
                <F label="CURP"><Input value={form.curp} onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })} /></F>
                <F label="NSS (IMSS)"><Input value={form.nss} onChange={(e) => setForm({ ...form, nss: e.target.value })} /></F>
                <F label="Cuenta bancaria (CLABE)"><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></F>
              </div>
              <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <F label="Frecuencia pago">
                  <Select value={form.salary_frequency} onValueChange={(v) => setForm({ ...form, salary_frequency: v })}>
                    <SelectTrigger data-testid="emp-freq"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="weekly">Semanal</SelectItem><SelectItem value="biweekly">Quincenal</SelectItem><SelectItem value="monthly">Mensual</SelectItem></SelectContent>
                  </Select>
                </F>
                <F label={`Sueldo base (${FREQ[form.salary_frequency]})`}><Input type="number" step="0.01" value={form.salary_base} onChange={(e) => setForm({ ...form, salary_base: e.target.value })} data-testid="emp-salary" /></F>
                <F label="Forma de pago">
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="transferencia">Transferencia</SelectItem><SelectItem value="efectivo">Efectivo</SelectItem><SelectItem value="cheque">Cheque</SelectItem></SelectContent>
                  </Select>
                </F>
              </div>
              <div className="border-t pt-3">
                <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-600 mb-2">Impuestos y descuentos</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.apply_isr} onChange={(e) => setForm({ ...form, apply_isr: e.target.checked })} className="w-4 h-4 rounded border-stone-300" />
                    <span className="text-sm">Aplicar ISR</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.apply_imss} onChange={(e) => setForm({ ...form, apply_imss: e.target.checked })} className="w-4 h-4 rounded border-stone-300" />
                    <span className="text-sm">Aplicar IMSS obrero</span>
                  </label>
                  <F label="INFONAVIT">
                    <Select value={form.infonavit_type} onValueChange={(v) => setForm({ ...form, infonavit_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin descuento</SelectItem>
                        <SelectItem value="percent">% del bruto</SelectItem>
                        <SelectItem value="fixed">Monto fijo</SelectItem>
                      </SelectContent>
                    </Select>
                  </F>
                  <F label="Valor INFONAVIT"><Input type="number" step="0.01" value={form.infonavit_value} onChange={(e) => setForm({ ...form, infonavit_value: e.target.value })} disabled={form.infonavit_type === "none"} /></F>
                </div>
              </div>
              <F label="Notas"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
              {editing && (
                <label className="flex items-center gap-2 cursor-pointer border-t pt-3">
                  <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 rounded border-stone-300" data-testid="emp-active-checkbox" />
                  <span className="text-sm">Empleado activo {form.active === false && <span className="text-amber-600">(desmarcado = desactivado, no aparece en corridas de nómina)</span>}</span>
                </label>
              )}
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-emp-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]">{editing ? "Guardar" : "Crear empleado"}</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Codes reveal modal */}
      {codes && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-[#1E3A8A]" />
              <h3 className="font-outfit font-bold text-lg">Credenciales · {codes.name}</h3>
            </div>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-stone-50 dark:bg-stone-900 border">
                <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1">PIN de checador</div>
                <div className="text-4xl font-outfit font-bold tabular-nums text-[#1E3A8A]">{codes.pin}</div>
                <div className="text-[10px] text-stone-500 mt-1">Anota o entrega al empleado</div>
              </div>
              <div className="p-4 rounded-lg bg-stone-50 dark:bg-stone-900 border">
                <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500 mb-1">Token QR</div>
                <div className="font-mono text-xs break-all text-stone-700">{codes.qr_token}</div>
                <div className="text-[10px] text-stone-500 mt-1">Puedes generar un QR con este token y pegarlo en el escritorio</div>
              </div>
              <Button onClick={() => setCodes(null)} className="w-full">Cerrar</Button>
            </div>
          </Card>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar a {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>Si tiene registros de asistencia o nómina, se desactivará en vez de eliminarse.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============================================================
// NÓMINA (CORRIDAS)
// ============================================================
const NominaTab = ({ periods, accounts, onChange, selected, setSelected }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === "dueno" || user?.role === "admin";
  const [detail, setDetail] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [openClose, setOpenClose] = useState(false);
  const empty = { period_type: "weekly", start_date: "", end_date: "", payment_date: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [accountId, setAccountId] = useState("");

  const openDetail = async (p) => {
    setSelected(p);
    const { data } = await api.get(`/payroll/periods/${p.id}`);
    setDetail(data);
  };
  const reloadDetail = async () => selected && openDetail(selected);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) return toast.error("Fechas requeridas");
    try {
      const { data } = await api.post("/payroll/periods", form);
      toast.success(`Período ${data.folio} creado (${data.employee_count} empleados)`);
      setForm(empty); setOpenNew(false); onChange();
      openDetail(data);
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const closePeriod = async () => {
    if (!accountId) return toast.error("Elige cuenta de tesorería");
    try {
      const { data } = await api.post(`/payroll/periods/${selected.id}/close`, { account_id: accountId });
      toast.success(`Nómina pagada · Total ${money(data.total_net)} · CxP ${data.purchase.folio}`);
      setOpenClose(false); setAccountId(""); onChange(); reloadDetail();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const updateLine = async (line, patch) => {
    try {
      await api.put(`/payroll/lines/${line.id}`, patch);
      reloadDetail();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const delPeriod = async (p) => {
    if (!window.confirm(`¿Eliminar período ${p.folio}?`)) return;
    try {
      await api.delete(`/payroll/periods/${p.id}`);
      toast.success("Período eliminado");
      setDetail(null); setSelected(null); onChange();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Corridas de nómina</h3>
        <Button onClick={() => setOpenNew(true)} data-testid="new-period-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Nueva corrida</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-1 border-stone-200 dark:border-stone-800 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b bg-stone-50 dark:bg-stone-900">
            <h4 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em]">Historial</h4>
          </div>
          <div className="divide-y max-h-[500px] overflow-auto">
            {periods.length === 0 ? <div className="p-6 text-center text-sm text-stone-500">Sin corridas. Crea la primera.</div> : periods.map((p) => (
              <button key={p.id} onClick={() => openDetail(p)} className={`w-full text-left p-4 hover:bg-stone-50 dark:hover:bg-stone-900/30 ${selected?.id === p.id ? "bg-blue-50 dark:bg-blue-950/20" : ""}`} data-testid={`period-row-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-outfit font-semibold text-sm text-[#1E3A8A]">{p.folio}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{p.start_date} → {p.end_date}</div>
                    <div className="text-[10px] text-stone-400 mt-0.5">{FREQ[p.period_type]} · {p.employee_count} empleados</div>
                  </div>
                  <div className="text-right">
                    {p.status === "closed" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Pagada</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700"><PlayCircle className="w-3 h-3" /> Abierta</span>
                    )}
                    {p.total_net > 0 && <div className="text-xs tabular-nums font-medium mt-1">{money(p.total_net)}</div>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-2 border-stone-200 dark:border-stone-800 shadow-sm overflow-hidden">
          {!detail ? (
            <div className="p-10 text-center">
              <Calendar className="w-12 h-12 mx-auto text-stone-300 mb-3" strokeWidth={1.5} />
              <div className="text-sm text-stone-500">Selecciona una corrida para ver el desglose</div>
            </div>
          ) : (
            <div>
              <div className="px-5 py-4 border-b bg-stone-50 dark:bg-stone-900 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-outfit font-bold text-lg text-[#1E3A8A]">{detail.period.folio}</span>
                    {detail.period.status === "closed" ? (
                      <span className="text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Pagada · <a href={`/cuentas-por-pagar`} className="underline">{detail.period.purchase_folio}</a></span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700">Abierta</span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500">{detail.period.start_date} → {detail.period.end_date} · Pago {detail.period.payment_date || "sin definir"}</div>
                </div>
                <div className="flex gap-2">
                  {detail.period.status === "open" && (
                    <>
                      {isAdmin && (
                        <Button variant="outline" onClick={() => delPeriod(detail.period)} data-testid="delete-period-btn" title="Eliminar corrida (sólo Admin/Dueño)" className="text-red-600 hover:text-red-700 border-red-200"><Trash2 className="w-4 h-4" /></Button>
                      )}
                      <Button onClick={() => setOpenClose(true)} data-testid="close-period-btn" className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="w-4 h-4 mr-1" /> Cerrar y pagar</Button>
                    </>
                  )}
                </div>
              </div>

              <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3 border-b">
                <MiniStat label="Bruto" value={money(detail.totals.gross_perceptions)} />
                <MiniStat label="ISR" value={money(detail.totals.isr)} tone="amber" />
                <MiniStat label="IMSS" value={money(detail.totals.imss)} tone="amber" />
                <MiniStat label="Neto" value={money(detail.totals.net_pay)} tone="emerald" big />
              </div>

              <div className="max-h-[400px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 dark:bg-stone-900 sticky top-0">
                    <tr>{["Empleado", "Días", "H. extra", "Faltas", "Bonos/Extra", "Bruto", "ISR", "IMSS", "INFONAVIT", "Extra ded.", "Préstamos", "Neto", ""].map((h) => <th key={h} className="px-2 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-500">{h}</th>)}</tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
                    {detail.lines.map((l) => (
                      <PayrollLineRow key={l.id} line={l} locked={detail.period.status === "closed"} onSave={(patch) => updateLine(l, patch)} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New period modal */}
      {openNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-6">
            <h3 className="font-outfit font-bold text-lg mb-4">Nueva corrida de nómina</h3>
            <form onSubmit={submitNew} className="space-y-3">
              <F label="Frecuencia">
                <Select value={form.period_type} onValueChange={(v) => setForm({ ...form, period_type: v })}>
                  <SelectTrigger data-testid="period-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="weekly">Semanal</SelectItem><SelectItem value="biweekly">Quincenal</SelectItem></SelectContent>
                </Select>
              </F>
              <div className="grid grid-cols-3 gap-3">
                <F label="Inicio *"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="period-start" /></F>
                <F label="Fin *"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="period-end" /></F>
                <F label="Fecha de pago"><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} /></F>
              </div>
              <F label="Notas"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
              <div className="p-3 rounded bg-blue-50 dark:bg-blue-950/20 text-xs text-stone-700 dark:text-stone-300">Se generarán líneas automáticamente para todos los empleados activos con frecuencia <b>{FREQ[form.period_type]}</b>. Días trabajados se toman del checador; puedes ajustar por línea.</div>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button type="button" variant="outline" onClick={() => setOpenNew(false)}>Cancelar</Button>
                <Button type="submit" data-testid="create-period-btn" className="bg-[#1E3A8A]">Crear</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Close period modal */}
      <AlertDialog open={openClose} onOpenChange={setOpenClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cerrar y pagar nómina {selected?.folio}</AlertDialogTitle>
            <AlertDialogDescription>Se generará una Compra en Cuentas por Pagar y un movimiento de egreso en la cuenta seleccionada. Los totales ya no se podrán editar después.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-3">
            <F label="Cuenta de tesorería origen">
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger data-testid="close-account"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency} · {money(a.balance || 0)}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            {detail && <div className="mt-3 text-sm">Total a pagar: <b className="tabular-nums text-emerald-700">{money(detail.totals.net_pay)}</b></div>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={closePeriod} className="bg-emerald-600 hover:bg-emerald-700">Cerrar y pagar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const PayrollLineRow = ({ line, locked, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [d, setD] = useState({ days_worked: line.days_worked, extra_hours: line.extra_hours, absences: line.absences, extra_perceptions: line.extra_perceptions, extra_deductions: line.extra_deductions });
  useEffect(() => { setD({ days_worked: line.days_worked, extra_hours: line.extra_hours, absences: line.absences, extra_perceptions: line.extra_perceptions, extra_deductions: line.extra_deductions }); }, [line]);
  const save = () => { onSave(d); setEditing(false); };
  return (
    <tr className="hover:bg-stone-50 dark:hover:bg-stone-900/30" data-testid={`line-${line.id}`}>
      <td className="px-2 py-2 text-xs">
        <div className="font-medium">{line.employee_name}</div>
        <div className="text-[10px] text-stone-500">{line.employee_position || "—"}</div>
      </td>
      <td className="px-2 py-2 text-xs tabular-nums">{editing ? <Input className="h-7 w-16" type="number" step="0.5" value={d.days_worked} onChange={(e) => setD({ ...d, days_worked: parseFloat(e.target.value) || 0 })} data-testid={`days-worked-${line.id}`} /> : line.days_worked}</td>
      <td className="px-2 py-2 text-xs tabular-nums">{editing ? <Input className="h-7 w-16" type="number" step="0.5" value={d.extra_hours} onChange={(e) => setD({ ...d, extra_hours: parseFloat(e.target.value) || 0 })} data-testid={`extra-hours-${line.id}`} /> : line.extra_hours}</td>
      <td className="px-2 py-2 text-xs tabular-nums">{editing ? <Input className="h-7 w-16" type="number" step="0.5" value={d.absences} onChange={(e) => setD({ ...d, absences: parseFloat(e.target.value) || 0 })} data-testid={`absences-${line.id}`} /> : line.absences}</td>
      <td className="px-2 py-2 text-xs tabular-nums" title="Bonos, comisiones u otras percepciones extra">
        {editing ? <Input className="h-7 w-20" type="number" step="0.01" value={d.extra_perceptions} onChange={(e) => setD({ ...d, extra_perceptions: parseFloat(e.target.value) || 0 })} data-testid={`extra-perceptions-${line.id}`} /> : (line.extra_perceptions > 0 ? <span className="text-emerald-700 font-medium">{money(line.extra_perceptions)}</span> : money(line.extra_perceptions))}
      </td>
      <td className="px-2 py-2 text-xs tabular-nums font-medium">{money(line.gross_perceptions)}</td>
      <td className="px-2 py-2 text-xs tabular-nums text-amber-700">{money(line.isr)}</td>
      <td className="px-2 py-2 text-xs tabular-nums text-amber-700">{money(line.imss)}</td>
      <td className="px-2 py-2 text-xs tabular-nums text-amber-700">{money(line.infonavit)}</td>
      <td className="px-2 py-2 text-xs tabular-nums">{editing ? <Input className="h-7 w-20" type="number" step="0.01" value={d.extra_deductions} onChange={(e) => setD({ ...d, extra_deductions: parseFloat(e.target.value) || 0 })} /> : money(line.extra_deductions)}</td>
      <td className="px-2 py-2 text-xs tabular-nums" title={(line.loan_breakdown || []).map((lb) => `${lb.description}: ${money(lb.amount)}`).join(" · ") || "Sin préstamos activos"}>
        {line.loan_deduction > 0 ? <span className="text-red-600 font-medium">{money(line.loan_deduction)}</span> : "—"}
      </td>
      <td className="px-2 py-2 text-sm tabular-nums font-bold text-emerald-700">{money(line.net_pay)}</td>
      <td className="px-2 py-2">
        <div className="flex gap-1">
          <a href={`/nomina/recibo/${line.id}`} target="_blank" rel="noopener noreferrer" className="p-1 rounded hover:bg-emerald-50 text-emerald-600" title="Recibo (imprimir/PDF)" data-testid={`receipt-${line.id}`}><FileText className="w-3.5 h-3.5" /></a>
          {!locked && (editing ? (
            <>
              <button onClick={save} className="p-1 rounded hover:bg-emerald-50 text-emerald-600" data-testid={`save-line-${line.id}`}><CheckCircle2 className="w-4 h-4" /></button>
              <button onClick={() => setEditing(false)} className="p-1 rounded hover:bg-stone-100 text-stone-500">×</button>
            </>
          ) : (
            <button onClick={() => setEditing(true)} className="p-1 rounded hover:bg-blue-50 text-[#1E3A8A]" data-testid={`edit-line-${line.id}`}><Pencil className="w-3.5 h-3.5" /></button>
          ))}
        </div>
      </td>
    </tr>
  );
};

// ============================================================
// PRÉSTAMOS Y DEDUCCIONES
// ============================================================
const PrestamosTab = ({ employees }) => {
  const [loans, setLoans] = useState([]);
  const [filter, setFilter] = useState("activo");
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { employee_id: "", concept_type: "prestamo", description: "", total_amount: "", installment_amount: "", num_installments: "", start_date: "" };
  const [form, setForm] = useState(empty);
  const [installmentMode, setInstallmentMode] = useState("amount"); // amount | weeks

  const load = async () => {
    const { data } = await api.get("/payroll/loans", { params: filter === "todos" ? {} : { status: filter } });
    setLoans(data);
  };
  useEffect(() => { load(); }, [filter]);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.total_amount) return toast.error("Empleado y monto total son requeridos");
    const body = {
      employee_id: form.employee_id,
      concept_type: form.concept_type,
      description: form.description,
      total_amount: parseFloat(form.total_amount),
      start_date: form.start_date || undefined,
    };
    if (installmentMode === "amount") body.installment_amount = parseFloat(form.installment_amount) || 0;
    else body.num_installments = parseInt(form.num_installments) || 0;
    try {
      await api.post("/payroll/loans", body);
      toast.success("Registrado. Se descontará automáticamente en cada nómina hasta liquidarse.");
      setForm(empty); setOpenNew(false); load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/payroll/loans/${editing.id}`, { installment_amount: parseFloat(editing.installment_amount), balance: parseFloat(editing.balance) });
      toast.success("Actualizado");
      setEditing(null); load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const cancelLoan = async (loan) => {
    if (!window.confirm(`¿Cancelar "${loan.description}" de ${loan.employee_name}? Ya no se descontará en próximas nóminas.`)) return;
    try {
      await api.post(`/payroll/loans/${loan.id}/cancel`);
      toast.success("Cancelado");
      load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const delLoan = async (loan) => {
    if (!window.confirm(`¿Eliminar "${loan.description}"?`)) return;
    try {
      await api.delete(`/payroll/loans/${loan.id}`);
      toast.success("Eliminado");
      load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const STATUS_BADGE = {
    activo: "bg-amber-100 text-amber-700",
    liquidado: "bg-emerald-100 text-emerald-700",
    cancelado: "bg-stone-200 text-stone-600",
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Préstamos y deducciones</h3>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-36" data-testid="loan-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="activo">Activos</SelectItem>
              <SelectItem value="liquidado">Liquidados</SelectItem>
              <SelectItem value="cancelado">Cancelados</SelectItem>
              <SelectItem value="todos">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setOpenNew(true)} data-testid="new-loan-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Registrar préstamo/deducción</Button>
      </div>

      <Card className="border-stone-200 dark:border-stone-800 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 dark:bg-stone-900">
            <tr>{["Empleado", "Tipo", "Descripción", "Total", "Cuota", "Pagado", "Saldo", "Estatus", ""].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-500">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
            {loans.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-stone-500">Sin registros en este filtro.</td></tr>
            ) : loans.map((l) => (
              <tr key={l.id} className="hover:bg-stone-50 dark:hover:bg-stone-900/30" data-testid={`loan-row-${l.id}`}>
                <td className="px-3 py-2 text-xs font-medium">{l.employee_name}</td>
                <td className="px-3 py-2 text-xs capitalize">{l.concept_type === "prestamo" ? "Préstamo" : "Otro"}</td>
                <td className="px-3 py-2 text-xs">{l.description}</td>
                <td className="px-3 py-2 text-xs tabular-nums">{money(l.total_amount)}</td>
                <td className="px-3 py-2 text-xs tabular-nums">{money(l.installment_amount)}</td>
                <td className="px-3 py-2 text-xs tabular-nums text-emerald-700">{money(l.amount_paid)}</td>
                <td className="px-3 py-2 text-xs tabular-nums font-semibold text-red-600">{money(l.balance)}</td>
                <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded ${STATUS_BADGE[l.status]}`}>{l.status}</span></td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {l.status === "activo" && (
                      <>
                        <button onClick={() => setEditing({ ...l })} className="p-1 rounded hover:bg-blue-50 text-[#1E3A8A]" title="Editar cuota/saldo" data-testid={`edit-loan-${l.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => cancelLoan(l)} className="p-1 rounded hover:bg-amber-50 text-amber-600" title="Cancelar" data-testid={`cancel-loan-${l.id}`}><X className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                    {l.amount_paid === 0 && (
                      <button onClick={() => delLoan(l)} className="p-1 rounded hover:bg-red-50 text-red-600" title="Eliminar" data-testid={`del-loan-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* New loan modal */}
      {openNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg p-6">
            <h3 className="font-outfit font-bold text-lg mb-4">Registrar préstamo / deducción</h3>
            <form onSubmit={submitNew} className="space-y-3">
              <F label="Empleado *">
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="loan-employee-select"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                  <SelectContent>{employees.filter((e) => e.active !== false).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Tipo">
                <Select value={form.concept_type} onValueChange={(v) => setForm({ ...form, concept_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="prestamo">Préstamo</SelectItem><SelectItem value="otro">Otra deducción / incidencia</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="Descripción"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ej. Préstamo para gastos médicos" data-testid="loan-description" /></F>
              <F label="Monto total *"><Input type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} data-testid="loan-total-amount" /></F>
              <div>
                <Label className="text-xs">¿Cómo defines la cuota?</Label>
                <div className="flex gap-2 mt-1.5">
                  <button type="button" onClick={() => setInstallmentMode("amount")} className={`px-3 py-1.5 rounded text-xs font-outfit font-medium ${installmentMode === "amount" ? "bg-[#1E3A8A] text-white" : "bg-stone-100 text-stone-600"}`}>Monto por semana</button>
                  <button type="button" onClick={() => setInstallmentMode("weeks")} className={`px-3 py-1.5 rounded text-xs font-outfit font-medium ${installmentMode === "weeks" ? "bg-[#1E3A8A] text-white" : "bg-stone-100 text-stone-600"}`}>Número de semanas</button>
                </div>
              </div>
              {installmentMode === "amount" ? (
                <F label="Cuota por semana/período *"><Input type="number" step="0.01" value={form.installment_amount} onChange={(e) => setForm({ ...form, installment_amount: e.target.value })} data-testid="loan-installment-amount" /></F>
              ) : (
                <F label="Número de semanas/períodos *"><Input type="number" step="1" value={form.num_installments} onChange={(e) => setForm({ ...form, num_installments: e.target.value })} data-testid="loan-num-installments" /></F>
              )}
              <F label="Fecha de inicio"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></F>
              <div className="p-3 rounded bg-blue-50 dark:bg-blue-950/20 text-xs text-stone-700 dark:text-stone-300">Se descontará automáticamente en cada nómina de este empleado hasta liquidar el saldo. Podrás editar la cuota o el saldo, o cancelarlo en cualquier momento.</div>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button type="button" variant="outline" onClick={() => setOpenNew(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-loan-btn" className="bg-[#1E3A8A]">Registrar</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Edit loan modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="font-outfit font-bold text-lg mb-1">Editar préstamo</h3>
            <p className="text-xs text-stone-500 mb-4">{editing.employee_name} · {editing.description}</p>
            <div className="space-y-3">
              <F label="Cuota por período"><Input type="number" step="0.01" value={editing.installment_amount} onChange={(e) => setEditing({ ...editing, installment_amount: e.target.value })} data-testid="edit-loan-installment" /></F>
              <F label="Saldo pendiente"><Input type="number" step="0.01" value={editing.balance} onChange={(e) => setEditing({ ...editing, balance: e.target.value })} data-testid="edit-loan-balance" /></F>
              <div className="text-[11px] text-stone-500">Si pones el saldo en 0, se marcará automáticamente como liquidado.</div>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button onClick={saveEdit} data-testid="save-loan-edit-btn" className="bg-[#1E3A8A]">Guardar</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============================================================
// BONOS (percepciones extra) — registro + historial completo
// ============================================================
const BonosTab = ({ employees }) => {
  const [bonuses, setBonuses] = useState([]);
  const [filter, setFilter] = useState("pendiente");
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { employee_id: "", description: "", amount: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/payroll/bonuses", { params: filter === "todos" ? {} : { status: filter } });
    setBonuses(data);
  };
  useEffect(() => { load(); }, [filter]);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.amount) return toast.error("Empleado y monto son requeridos");
    try {
      await api.post("/payroll/bonuses", { employee_id: form.employee_id, description: form.description, amount: parseFloat(form.amount) });
      toast.success("Registrado. Se aplicará automáticamente en la próxima nómina abierta de este empleado.");
      setForm(empty); setOpenNew(false); load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/payroll/bonuses/${editing.id}`, { description: editing.description, amount: parseFloat(editing.amount) });
      toast.success("Actualizado");
      setEditing(null); load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const cancelBonus = async (b) => {
    if (!window.confirm(`¿Cancelar "${b.description}" de ${b.employee_name}?`)) return;
    try { await api.post(`/payroll/bonuses/${b.id}/cancel`); toast.success("Cancelado"); load(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const delBonus = async (b) => {
    if (!window.confirm(`¿Eliminar "${b.description}"?`)) return;
    try { await api.delete(`/payroll/bonuses/${b.id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const STATUS_BADGE = {
    pendiente: "bg-blue-100 text-[#1E3A8A]",
    aplicado: "bg-emerald-100 text-emerald-700",
    cancelado: "bg-stone-200 text-stone-600",
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <h3 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Bonos y percepciones extra</h3>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-36" data-testid="bonus-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pendiente">Pendientes</SelectItem>
              <SelectItem value="aplicado">Aplicados</SelectItem>
              <SelectItem value="cancelado">Cancelados</SelectItem>
              <SelectItem value="todos">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setOpenNew(true)} data-testid="new-bonus-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF]"><Plus className="w-4 h-4 mr-1.5" /> Registrar bono</Button>
      </div>

      <Card className="border-stone-200 dark:border-stone-800 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 dark:bg-stone-900">
            <tr>{["Empleado", "Descripción", "Monto", "Estatus", "Período aplicado", "Fecha", ""].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-wider text-stone-500">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
            {bonuses.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-stone-500">Sin registros en este filtro.</td></tr>
            ) : bonuses.map((b) => (
              <tr key={b.id} className="hover:bg-stone-50 dark:hover:bg-stone-900/30" data-testid={`bonus-row-${b.id}`}>
                <td className="px-3 py-2 text-xs font-medium">{b.employee_name}</td>
                <td className="px-3 py-2 text-xs">{b.description}</td>
                <td className="px-3 py-2 text-xs tabular-nums text-emerald-700 font-semibold">{money(b.amount)}</td>
                <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded ${STATUS_BADGE[b.status]}`}>{b.status}</span></td>
                <td className="px-3 py-2 text-xs text-[#1E3A8A]">{b.period_folio || "—"}</td>
                <td className="px-3 py-2 text-xs text-stone-500">{new Date(b.created_at).toLocaleDateString("es-MX")}</td>
                <td className="px-3 py-2">
                  {b.status === "pendiente" && (
                    <div className="flex gap-1">
                      <button onClick={() => setEditing({ ...b })} className="p-1 rounded hover:bg-blue-50 text-[#1E3A8A]" title="Editar" data-testid={`edit-bonus-${b.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => cancelBonus(b)} className="p-1 rounded hover:bg-amber-50 text-amber-600" title="Cancelar" data-testid={`cancel-bonus-${b.id}`}><X className="w-3.5 h-3.5" /></button>
                      <button onClick={() => delBonus(b)} className="p-1 rounded hover:bg-red-50 text-red-600" title="Eliminar" data-testid={`del-bonus-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {openNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6">
            <h3 className="font-outfit font-bold text-lg mb-4">Registrar bono</h3>
            <form onSubmit={submitNew} className="space-y-3">
              <F label="Empleado *">
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="bonus-employee-select"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                  <SelectContent>{employees.filter((e) => e.active !== false).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Descripción"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ej. Bono de puntualidad" data-testid="bonus-description" /></F>
              <F label="Monto *"><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="bonus-amount" /></F>
              <div className="p-3 rounded bg-blue-50 dark:bg-blue-950/20 text-xs text-stone-700 dark:text-stone-300">Se aplicará automáticamente como percepción extra en la próxima nómina abierta de este empleado (monto libre, sin control de saldo).</div>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button type="button" variant="outline" onClick={() => setOpenNew(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-bonus-btn" className="bg-[#1E3A8A]">Registrar</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm p-6">
            <h3 className="font-outfit font-bold text-lg mb-1">Editar bono</h3>
            <p className="text-xs text-stone-500 mb-4">{editing.employee_name}</p>
            <div className="space-y-3">
              <F label="Descripción"><Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="edit-bonus-description" /></F>
              <F label="Monto"><Input type="number" step="0.01" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="edit-bonus-amount" /></F>
              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button onClick={saveEdit} data-testid="save-bonus-edit-btn" className="bg-[#1E3A8A]">Guardar</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============================================================
// REPORTE ASISTENCIA
// ============================================================
const ReporteAsistenciaTab = ({ employees }) => {
  const [empId, setEmpId] = useState("all");
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [dayRows, setDayRows] = useState([]);

  const load = async () => {
    const params = { date_from: from, date_to: to, limit: 5000 };
    if (empId !== "all") params.employee_id = empId;
    const { data } = await api.get("/attendance", { params });
    setRows(data);

    const hsParams = { date_from: from, date_to: to };
    if (empId !== "all") hsParams.employee_id = empId;
    const { data: summaries } = await api.get("/attendance/hours-summary", { params: hsParams });
    const flat = [];
    summaries.forEach((s) => {
      s.days.forEach((d) => flat.push({ ...d, employee_name: s.employee_name, standard_daily_hours: s.standard_daily_hours }));
    });
    flat.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : a.employee_name.localeCompare(b.employee_name)));
    setDayRows(flat);
  };
  useEffect(() => { load(); }, [empId, from, to]);

  return (
    <div className="space-y-5">
      <Card className="p-4 border-stone-200 dark:border-stone-800 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <F label="Empleado">
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Desde"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></F>
          <F label="Hasta"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></F>
          <div className="flex items-end text-xs text-stone-500">{rows.length} eventos</div>
        </div>
      </Card>

      <Card className="border-stone-200 dark:border-stone-800 shadow-sm overflow-hidden" data-testid="hours-worked-card">
        <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-800">
          <h4 className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100">Horas trabajadas por día</h4>
          <p className="text-xs text-stone-500 mt-0.5">Calculadas emparejando entrada/salida de cada empleado en el rango seleccionado.</p>
        </div>
        <div className="max-h-[400px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900 sticky top-0">
              <tr>{["Fecha", "Empleado", "Entrada", "Salida", "Horas trabajadas", "Extra del día", "Estado"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
              {dayRows.length === 0 ? <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-stone-500">Sin días con registros en el rango.</td></tr> : dayRows.map((d, i) => (
                <tr key={i} data-testid={`hours-day-row-${i}`}>
                  <td className="px-3 py-2 text-xs">{d.date}</td>
                  <td className="px-3 py-2 text-sm font-medium">{d.employee_name}</td>
                  <td className="px-3 py-2 text-xs font-mono">{d.first_in || "—"}</td>
                  <td className="px-3 py-2 text-xs font-mono">{d.last_out || "—"}</td>
                  <td className="px-3 py-2 text-sm tabular-nums font-semibold">{d.incomplete ? "—" : `${d.hours_worked.toFixed(2)} h`}</td>
                  <td className="px-3 py-2 text-sm tabular-nums">{!d.incomplete && d.daily_overtime > 0 ? <span className="text-amber-700 dark:text-amber-400 font-semibold">+{d.daily_overtime.toFixed(2)} h</span> : <span className="text-stone-400">—</span>}</td>
                  <td className="px-3 py-2">
                    {d.incomplete
                      ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700">Incompleto</span>
                      : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">Completo</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="border-stone-200 dark:border-stone-800 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 dark:border-stone-800">
          <h4 className="font-outfit font-semibold text-sm text-stone-900 dark:text-stone-100">Bitácora de eventos crudos</h4>
        </div>
        <div className="max-h-[400px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900 sticky top-0">
              <tr>{["Fecha", "Hora", "Foto", "Empleado", "Tipo", "Método", "Registró"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
              {rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-14 text-center text-sm text-stone-500">Sin registros en el rango.</td></tr> : rows.map((a) => (
                <tr key={a.id}>
                  <td className="px-3 py-2 text-xs">{a.movement_date}</td>
                  <td className="px-3 py-2 text-xs font-mono">{a.time}</td>
                  <td className="px-3 py-2"><AttendancePhoto path={a.photo_path} /></td>
                  <td className="px-3 py-2 text-sm font-medium">{a.employee_name}</td>
                  <td className="px-3 py-2">
                    {a.kind === "in" ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700"><LogIn className="w-3 h-3" /> Entrada</span> : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700"><LogOut className="w-3 h-3" /> Salida</span>}
                  </td>
                  <td className="px-3 py-2 text-xs uppercase tracking-wider text-stone-500">{a.method}</td>
                  <td className="px-3 py-2 text-xs text-stone-500">{a.recorded_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// HORAS EXTRA TAB: quién superó su jornada estándar, diario y semanal
// ============================================================
const HorasExtraTab = ({ employees }) => {
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [summaries, setSummaries] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance/hours-summary", { params: { date_from: from, date_to: to } });
      data.sort((a, b) => b.weekly_overtime_hours - a.weekly_overtime_hours);
      setSummaries(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [from, to]);

  const withOvertime = summaries.filter((s) => s.weekly_overtime_hours > 0);

  return (
    <div className="space-y-5">
      <Card className="p-4 border-stone-200 dark:border-stone-800 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <F label="Desde"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="hex-from" /></F>
          <F label="Hasta"><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="hex-to" /></F>
          <div className="md:col-span-2 flex items-end">
            <p className="text-xs text-stone-500">La jornada estándar se configura por empleado en la pestaña <b>Empleados</b>. El total de horas extra de esta semana se autocompletará en el campo "Horas extra" al generar la corrida de nómina semanal (editable si hace falta ajustar).</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniStat label="Empleados con horas extra" value={withOvertime.length} tone="amber" big />
        <MiniStat label="Horas extra totales (rango)" value={`${summaries.reduce((a, s) => a + s.weekly_overtime_hours, 0).toFixed(1)} h`} tone="amber" big />
        <MiniStat label="Empleados en el rango" value={summaries.length} tone="stone" big />
      </div>

      <Card className="border-stone-200 dark:border-stone-800 shadow-sm overflow-hidden" data-testid="horas-extra-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900">
              <tr>{["", "Empleado", "Jornada estándar", "Días trabajados", "Horas totales", "Horas extra (rango)"].map((h) => <th key={h} className="px-3 py-2.5 text-left text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-stone-500" data-testid="horas-extra-loading">Calculando…</td></tr>
              ) : summaries.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-stone-500">Sin empleados activos o sin registros en el rango.</td></tr>
              ) : summaries.map((s) => (
                <React.Fragment key={s.employee_id}>
                  <tr className={s.weekly_overtime_hours > 0 ? "bg-amber-50/60 dark:bg-amber-950/10" : ""} data-testid={`hex-row-${s.employee_id}`}>
                    <td className="px-3 py-2">
                      <button onClick={() => setExpanded(expanded === s.employee_id ? null : s.employee_id)} className="p-1 rounded hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500" data-testid={`hex-expand-${s.employee_id}`}>
                        {expanded === s.employee_id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-medium">{s.employee_name}</td>
                    <td className="px-3 py-2 text-xs text-stone-500 tabular-nums">{s.standard_daily_hours} h/día</td>
                    <td className="px-3 py-2 text-xs tabular-nums">{s.days_worked}</td>
                    <td className="px-3 py-2 tabular-nums font-semibold">{s.total_hours.toFixed(2)} h</td>
                    <td className="px-3 py-2 tabular-nums">
                      {s.weekly_overtime_hours > 0
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold" data-testid={`hex-overtime-${s.employee_id}`}><Clock3 className="w-3.5 h-3.5" /> +{s.weekly_overtime_hours.toFixed(2)} h</span>
                        : <span className="text-stone-400">Sin excedente</span>}
                    </td>
                  </tr>
                  {expanded === s.employee_id && (
                    <tr>
                      <td colSpan={6} className="px-3 py-3 bg-stone-50 dark:bg-stone-900/40">
                        {s.days.length === 0 ? <div className="text-xs text-stone-500 px-2">Sin días con registros en el rango.</div> : (
                          <table className="w-full text-xs ml-6" style={{ width: "calc(100% - 1.5rem)" }}>
                            <thead><tr className="text-stone-500">{["Fecha", "Entrada", "Salida", "Horas", "Extra del día", "Estado"].map((h) => <th key={h} className="text-left font-outfit font-semibold uppercase tracking-wider pb-1 pr-3">{h}</th>)}</tr></thead>
                            <tbody>
                              {s.days.map((d) => (
                                <tr key={d.date} data-testid={`hex-day-${s.employee_id}-${d.date}`}>
                                  <td className="py-0.5 pr-3">{d.date}</td>
                                  <td className="py-0.5 pr-3 font-mono">{d.first_in || "—"}</td>
                                  <td className="py-0.5 pr-3 font-mono">{d.last_out || "—"}</td>
                                  <td className="py-0.5 pr-3 font-semibold">{d.incomplete ? "—" : `${d.hours_worked.toFixed(2)} h`}</td>
                                  <td className="py-0.5 pr-3">{!d.incomplete && d.daily_overtime > 0 ? <span className="text-amber-700 font-medium">+{d.daily_overtime.toFixed(2)} h</span> : "—"}</td>
                                  <td className="py-0.5 pr-3">{d.incomplete ? <span className="text-red-600">Incompleto</span> : <span className="text-emerald-600">Completo</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// UI HELPERS
// ============================================================
const TONES = {
  blue: "bg-[#1E3A8A]/10 text-[#1E3A8A] dark:text-blue-400",
  amber: "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400",
  emerald: "bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400",
  stone: "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300",
};

const KPI = ({ icon: Icon, label, value, tone = "blue" }) => (
  <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">{label}</div>
        <div className="mt-1 font-outfit font-semibold text-xl text-stone-900 dark:text-stone-100 tabular-nums truncate">{value}</div>
      </div>
      <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${TONES[tone] || TONES.blue}`}><Icon className="w-4 h-4" strokeWidth={1.75} /></div>
    </div>
  </Card>
);

const MiniStat = ({ label, value, tone = "stone", big }) => {
  const toneClass = tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-stone-900";
  return (
    <div className="p-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-900/30">
      <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-500">{label}</div>
      <div className={`${big ? "text-2xl font-bold" : "text-lg font-semibold"} font-outfit tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
};

const F = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-600 dark:text-stone-400">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
